/**
 * Groups Rebuy Smart Cart cross-sell widgets into one horizontal slider.
 * MutationObserver watches Rebuy recs childList only, never theme-owned DOM.
 */
{
  const CART_SELECTOR = '#rebuy-cart';
  const RECS_SELECTOR = '.rebuy-cart__flyout-recommendations';
  const HEADER_CLASS = 'fuel-30-upsell__header';
  const TRACK_CLASS = 'fuel-30-upsell__track';
  const SCROLLBAR_CLASS = 'fuel-30-upsell__scrollbar';
  const THUMB_CLASS = 'fuel-30-upsell__scrollbar-thumb';
  const HEADER_COPY = "Save More, Don't Miss Out";
  const SAVE_ATTR = 'data-fuel-30-save';
  const SAVE_CLASS = 'fuel-30-upsell__save';
  const TEST_CLONE_ATTR = 'data-fuel-30-test-clone';
  const TEST_MIN_SLIDES = 6;
  // Preview-only. Set true to pad the slider to 6 tiles so swipe can be checked.
  const TEST_DUPLICATE_SLIDES = false;

  let applying = false;
  let scheduled = false;
  let recsObserved = null;
  let bootObserver = null;
  let scrollAbort = null;

  const toCents = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') {
      if (!Number.isFinite(value) || value <= 0) return null;
      return Math.round(value);
    }
    const raw = String(value).trim();
    if (!raw) return null;
    const amount = parseFloat(raw.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) return null;
    return raw.includes('.') ? Math.round(amount * 100) : Math.round(amount);
  };

  const savePercentFromCents = (compareCents, saleCents) => {
    if (compareCents == null || saleCents == null || compareCents <= saleCents) return null;
    const pct = Math.round(((compareCents - saleCents) * 100) / compareCents);
    return pct > 0 ? pct : null;
  };

  const formatSaveLabel = (pct) => {
    if (Number.isInteger(pct)) return `Save ${pct}%`;
    const value = Math.round(pct * 10) / 10;
    return `Save ${value}%`;
  };

  const parseSavePercentFromText = (text) => {
    const match = text.match(/save\s+(\d+(?:\.\d+)?)\s*%/i);
    if (!match) return null;
    const value = parseFloat(match[1]);
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const parseMoneyText = (text) => {
    if (!text) return null;
    const match = text.trim().replace(/\s/g, '').match(/[\d,.]+/);
    if (!match) return null;
    let numStr = match[0];
    if (numStr.includes(',') && numStr.includes('.')) {
      numStr = numStr.replace(/,/g, '');
    } else if (numStr.includes(',') && !numStr.includes('.')) {
      const parts = numStr.split(',');
      numStr = parts.length === 2 && parts[1].length <= 2 ? parts.join('.') : numStr.replace(/,/g, '');
    }
    const value = parseFloat(numStr);
    return Number.isFinite(value) ? value : null;
  };

  const moneyFromEl = (el) => {
    if (!el) return null;
    const visible = el.querySelector('span:not(.sr-only)') || el;
    return parseMoneyText(visible.textContent);
  };

  const productIdFromBlock = (block) => {
    for (const cls of block.classList) {
      if (cls.startsWith('product-id-')) return cls.slice('product-id-'.length);
    }
    return null;
  };

  const productHandleFromBlock = (block) => {
    for (const cls of block.classList) {
      if (cls === 'rebuy-product-block' || cls.startsWith('product-id-') || cls === 'cart-has-item') continue;
      if (/^[a-z0-9-]+$/.test(cls)) return cls;
    }
    return null;
  };

  const widgetFromWrap = (wrap) => {
    const id = wrap.getAttribute('data-rebuy-id') || wrap.dataset.rebuyId;
    if (!id) return null;
    const widgets = window.Rebuy?.widgets;
    if (!Array.isArray(widgets)) return null;
    return widgets.find((widget) => String(widget.id) === String(id)) || null;
  };

  const selectedVariant = (product) => {
    if (product.selected_variant) return product.selected_variant;
    const variantId = product.selected_variant_id;
    if (variantId == null) return product.variants?.[0] || null;
    return product.variants?.find((variant) => String(variant.id) === String(variantId)) || product.variants?.[0] || null;
  };

  const productFromWidget = (widget, block) => {
    const products = widget.products || widget.data?.products || [];
    if (!products.length) return null;
    const productId = productIdFromBlock(block);
    if (productId) {
      const byId = products.find((entry) => String(entry.id) === String(productId));
      if (byId) return byId;
    }
    const handle = productHandleFromBlock(block);
    if (handle) {
      const byHandle = products.find((entry) => entry.handle === handle);
      if (byHandle) return byHandle;
    }
    return products.length === 1 ? products[0] : null;
  };

  const priceRoot = (block) => block.querySelector('.rebuy-product-price');

  const displayedSaleDollars = (block) => {
    const root = priceRoot(block);
    if (!root) return null;
    const saleEl = root.querySelector('.rebuy-money.sale') || root.querySelector('.rebuy-money:not(.compare-at)');
    return moneyFromEl(saleEl);
  };

  const savePercentFromDom = (block) => {
    const root = priceRoot(block);
    if (!root) return null;
    const compare = moneyFromEl(root.querySelector('.rebuy-money.compare-at'));
    const sale = displayedSaleDollars(block);
    if (compare == null || sale == null) return null;
    return savePercentFromCents(Math.round(compare * 100), Math.round(sale * 100));
  };

  const savePercentFromUtil = (widget, product) => {
    const util = window.Rebuy?.util;
    if (!util || typeof util.calculateDiscount !== 'function' || !product) return null;
    const options =
      widget?.data?.discount_options || widget?.data?.discounts || widget?.discounts || widget?.data?.discount || [];
    if (!Array.isArray(options) || !options.length) return null;
    try {
      const result = util.calculateDiscount(product, true, options);
      if (!result?.shouldShowDiscountedPrice) return null;
      if (result.type === 'percentage' && Number(result.savingAmount) > 0) {
        return Math.round(Number(result.savingAmount));
      }
      const compare = toCents(result.compareAtPrice);
      const sale = toCents(result.discountedPrice);
      return savePercentFromCents(compare, sale);
    } catch {
      return null;
    }
  };

  const savePercentFromWidget = (widget, block) => {
    const product = productFromWidget(widget, block);
    if (!product) return null;
    const variant = selectedVariant(product);
    if (!variant) return null;

    const fromUtil = savePercentFromUtil(widget, product);
    if (fromUtil != null) return fromUtil;

    const sale =
      typeof widget.variantPrice === 'function'
        ? toCents(widget.variantPrice(product, variant))
        : toCents(variant.price);
    if (sale == null) return null;

    let compare =
      typeof widget.variantCompareAtPrice === 'function'
        ? toCents(widget.variantCompareAtPrice(product, variant))
        : null;
    if (compare == null || compare <= sale) {
      compare = toCents(variant.compare_at_price ?? variant.compareAtPrice);
    }
    if (compare == null || compare <= sale) {
      compare = toCents(variant.price);
    }
    if (compare == null || compare <= sale) return null;

    return savePercentFromCents(compare, sale);
  };

  const savePercentFromRebuyLabel = (block) => {
    const label = block.querySelector('.rebuy-product-label');
    return parseSavePercentFromText(label?.textContent ?? '');
  };

  const savePercentFromWidgetTitle = (wrap, block) => {
    const title = wrap.querySelector('.primary-title')?.textContent?.trim() ?? '';
    const fromTitle = parseSavePercentFromText(title);
    if (fromTitle != null) return fromTitle;

    const dollarMatch = title.match(/save\s+\$?\s*([\d,.]+)/i);
    if (!dollarMatch) return null;
    const saveDollars = parseMoneyText(dollarMatch[1]);
    const sale = displayedSaleDollars(block);
    if (saveDollars == null || sale == null) return null;
    const compareDollars = sale + saveDollars;
    return savePercentFromCents(Math.round(compareDollars * 100), Math.round(sale * 100));
  };

  const blockSavePercent = (block, wrap) => {
    const fromDom = savePercentFromDom(block);
    if (fromDom != null) return fromDom;

    const widget = widgetFromWrap(wrap);
    const fromWidget = widget ? savePercentFromWidget(widget, block) : null;
    if (fromWidget != null) return fromWidget;

    const fromLabel = savePercentFromRebuyLabel(block);
    if (fromLabel != null) return fromLabel;

    return savePercentFromWidgetTitle(wrap, block);
  };

  const blockSaveLabel = (block, wrap) => {
    const pct = blockSavePercent(block, wrap);
    return pct == null ? null : formatSaveLabel(pct);
  };

  const isDisplayedBlock = (block) => {
    if (!block.isConnected) return false;
    return getComputedStyle(block).display !== 'none';
  };

  const recsRoot = () => document.querySelector(`${CART_SELECTOR} ${RECS_SELECTOR}`);

  const visibleWidgetWraps = (recs) =>
    [...recs.querySelectorAll(`[data-rebuy-id]:not([${TEST_CLONE_ATTR}])`)].filter((wrap) =>
      !!wrap.querySelector('.rebuy-widget.is-visible .rebuy-product-block')
    );

  const unwrapTrack = (recs) => {
    const track = recs?.querySelector(`:scope > .${TRACK_CLASS}`);
    if (!track) return;
    while (track.firstChild) recs.insertBefore(track.firstChild, track);
    track.remove();
  };

  const ensureTrack = (recs) => {
    let track = recs.querySelector(`:scope > .${TRACK_CLASS}`);
    if (!track) {
      track = document.createElement('div');
      track.className = TRACK_CLASS;
    }

    const header = recs.querySelector(`:scope > .${HEADER_CLASS}`);
    const alreadyPlaced = header
      ? track.parentElement === recs && header.nextElementSibling === track
      : track.parentElement === recs;
    if (!alreadyPlaced) {
      if (header) recs.insertBefore(track, header.nextSibling);
      else recs.appendChild(track);
    }

    for (const wrap of recs.querySelectorAll(':scope > [data-rebuy-id]')) {
      track.appendChild(wrap);
    }
    return track;
  };

  const syncScrollbar = (track, bar) => {
    const thumb = bar.querySelector(`.${THUMB_CLASS}`);
    if (!thumb) return;
    const view = track.clientWidth;
    const total = track.scrollWidth;
    const max = total - view;
    if (max <= 1) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;
    const barW = bar.clientWidth;
    const thumbW = Math.max((view / total) * barW, 1);
    const x = (track.scrollLeft / max) * (barW - thumbW);
    thumb.style.width = `${thumbW}px`;
    thumb.style.transform = `translateX(${x}px)`;
  };

  const ensureScrollbar = (recs, track) => {
    let bar = recs.querySelector(`:scope > .${SCROLLBAR_CLASS}`);
    if (!bar) {
      bar = document.createElement('div');
      bar.className = SCROLLBAR_CLASS;
      const thumb = document.createElement('span');
      thumb.className = THUMB_CLASS;
      bar.appendChild(thumb);
    }
    if (bar.previousElementSibling !== track) recs.insertBefore(bar, track.nextSibling);

    if (track.dataset.fuel30ScrollBound === '1') {
      requestAnimationFrame(() => syncScrollbar(track, bar));
      return;
    }

    scrollAbort?.abort();
    scrollAbort = new AbortController();
    const { signal } = scrollAbort;
    const sync = () => syncScrollbar(track, bar);
    track.addEventListener('scroll', sync, { passive: true, signal });
    bar.addEventListener(
      'click',
      (event) => {
        const rect = bar.getBoundingClientRect();
        const ratio = (event.clientX - rect.left) / rect.width;
        track.scrollLeft = ratio * (track.scrollWidth - track.clientWidth);
      },
      { signal }
    );
    track.dataset.fuel30ScrollBound = '1';
    requestAnimationFrame(sync);
  };

  const removeTestClones = (recs) => {
    recs?.querySelectorAll(`[${TEST_CLONE_ATTR}]`).forEach((el) => el.remove());
  };

  const padSlidesForPreview = (recs) => {
    if (!TEST_DUPLICATE_SLIDES) {
      removeTestClones(recs);
      return;
    }

    const track = recs.querySelector(`:scope > .${TRACK_CLASS}`);
    if (!track) return;

    const tiles = [...track.querySelectorAll(`:scope > [data-rebuy-id]:not([${TEST_CLONE_ATTR}])`)].filter(
      (wrap) => wrap.querySelector('.rebuy-widget.is-visible .rebuy-product-block')
    );
    const clones = track.querySelectorAll(`:scope > [${TEST_CLONE_ATTR}]`);
    const needed = Math.max(0, TEST_MIN_SLIDES - tiles.length);
    if (clones.length === needed) return;

    removeTestClones(recs);
    if (needed <= 0 || !tiles.length) return;

    for (let i = 0; i < needed; i++) {
      const clone = tiles[i % tiles.length].cloneNode(true);
      clone.setAttribute(TEST_CLONE_ATTR, '');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('button').forEach((btn) => {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      });
      clone.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', (event) => event.preventDefault());
      });
      track.appendChild(clone);
    }
  };

  const apply = (recs) => {
    const wraps = visibleWidgetWraps(recs);
    if (!wraps.length) {
      recs?.querySelector(`:scope > .${HEADER_CLASS}`)?.remove();
      recs?.querySelector(`:scope > .${SCROLLBAR_CLASS}`)?.remove();
      unwrapTrack(recs);
      return;
    }

    let header = recs.querySelector(`:scope > .${HEADER_CLASS}`);
    if (!header) {
      header = document.createElement('p');
      header.className = HEADER_CLASS;
    }
    if (header.parentElement !== recs || recs.firstElementChild !== header) {
      recs.insertBefore(header, recs.firstChild);
    }
    if (header.textContent !== HEADER_COPY) header.textContent = HEADER_COPY;
    if (header.hidden) header.hidden = false;

    for (const wrap of wraps) {
      for (const block of wrap.querySelectorAll('.rebuy-product-block')) {
        const title = block.querySelector('.rebuy-product-title');
        let pill = block.querySelector(`.${SAVE_CLASS}`);
        const save = isDisplayedBlock(block) ? blockSaveLabel(block, wrap) : null;
        if (save && title) {
          if (block.getAttribute(SAVE_ATTR) !== save) block.setAttribute(SAVE_ATTR, save);
          if (!pill) {
            pill = document.createElement('span');
            pill.className = SAVE_CLASS;
          }
          if (pill.textContent !== save) pill.textContent = save;
          if (title.nextElementSibling !== pill) title.after(pill);
        } else {
          if (block.hasAttribute(SAVE_ATTR)) block.removeAttribute(SAVE_ATTR);
          pill?.remove();
        }
      }
    }

    const track = ensureTrack(recs);
    padSlidesForPreview(recs);
    ensureScrollbar(recs, track);
  };

  const observer = new MutationObserver(() => scheduleRun());

  const bindObservers = () => {
    const recs = recsRoot();
    if (!recs || recs === recsObserved) return !!recs;
    observer.observe(recs, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    recsObserved = recs;
    return true;
  };

  const run = () => {
    if (applying) return;
    applying = true;
    observer.disconnect();
    recsObserved = null;
    try {
      const recs = recsRoot();
      if (recs) apply(recs);
    } finally {
      observer.takeRecords();
      bindObservers();
      applying = false;
    }
  };

  const scheduleRun = () => {
    if (scheduled || applying) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      run();
    });
  };

  if (!bindObservers()) {
    bootObserver = new MutationObserver(() => {
      if (bindObservers()) {
        bootObserver.disconnect();
        scheduleRun();
      }
    });
    bootObserver.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('rebuy:cart.change', scheduleRun);
  document.addEventListener('rebuy.cart.change', scheduleRun);
  document.addEventListener('rebuy.ready', scheduleRun);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRun);
  } else {
    scheduleRun();
  }
}
